import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Flame,
  Heart,
  Skull,
  Sword,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CATEGORY_LABELS,
  type DuelCategory,
  type DuelQuestion,
} from "@/games/chemDuel/questions";
import {
  STARTING_HP,
  TURN_SECONDS,
  drawQuestion,
  isSuddenDeath,
  resolveAnswer,
  type ResolvedMove,
} from "@/games/chemDuel/engine";

const ALL_CATEGORIES: DuelCategory[] = ["bonding", "reactions", "organic", "safety"];
const RESULT_PAUSE_MS = 2200;

type Phase =
  | { kind: "answering"; question: DuelQuestion; startedAt: number }
  | { kind: "result"; question: DuelQuestion; chosenIndex: number | null; resolved: ResolvedMove }
  | { kind: "handoff"; nextPlayer: PlayerId };

/**
 * Chem Duel - HP-based hot-seat trivia.
 *
 * Flow per turn:
 *  answering -> result (2.2s) -> handoff -> answering (next player) ...
 * Match ends when someone hits 0 HP (or both players agree to forfeit via Quit).
 */
export default function ChemDuel() {
  const navigate = useNavigate();
  const match = useHotSeatMatch(STARTING_HP);

  const [phase, setPhase] = useState<Phase | null>(null);
  const [askedIds, setAskedIds] = useState<Set<string>>(() => new Set());
  const [enabledCategories, setEnabledCategories] = useState<Set<DuelCategory>>(
    () => new Set(ALL_CATEGORIES),
  );
  const handoffTimerRef = useRef<number | null>(null);

  // Cleanup any pending timers on unmount.
  useEffect(() => {
    return () => {
      if (handoffTimerRef.current) window.clearTimeout(handoffTimerRef.current);
    };
  }, []);

  // -------- match lifecycle --------

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      match.start(p1, p2);
      setAskedIds(new Set());
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const drawNext = useCallback(
    (forPlayer: PlayerId): DuelQuestion => {
      const hardOnly = isSuddenDeath(
        match.state.turnNumber,
        match.state.stats[0],
        match.state.stats[1],
      );
      // Note: forPlayer not currently used for selection; reserved for future
      // per-player category prefs.
      void forPlayer;
      const q = drawQuestion(Math.random, {
        exclude: askedIds,
        hardOnly,
        categories: enabledCategories,
      });
      return q;
    },
    [askedIds, enabledCategories, match.state.turnNumber, match.state.stats],
  );

  const beginTurn = useCallback(() => {
    const q = drawNext(match.state.currentTurn);
    setAskedIds((prev) => new Set(prev).add(q.id));
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, [drawNext, match.state.currentTurn]);

  // -------- answer handling --------

  const submitAnswer = useCallback(
    (chosenIndex: number | null) => {
      if (!phase || phase.kind !== "answering") return;
      const elapsed = performance.now() - phase.startedAt;
      const resolved = resolveAnswer(chosenIndex, phase.question, elapsed);

      const current = match.state.currentTurn;
      const opponent: PlayerId = current === 0 ? 1 : 0;

      // Apply damage. HP can't go below 0 visually.
      const newCurrentHp = Math.max(0, match.state.stats[current] - resolved.selfDamage);
      const newOpponentHp = Math.max(
        0,
        match.state.stats[opponent] - resolved.opponentDamage,
      );
      match.setStat(current, newCurrentHp);
      match.setStat(opponent, newOpponentHp);

      setPhase({
        kind: "result",
        question: phase.question,
        chosenIndex,
        resolved,
      });

      // Audio feedback for the answer outcome.
      if (resolved.outcome === "correct-fast" || resolved.outcome === "correct-slow") {
        play("correct");
      } else if (resolved.outcome === "wrong") {
        play("wrong");
      }
      // Note: "timeout" already plays the expire SFX from TurnTimer.

      // Schedule transition to handoff (or finish).
      handoffTimerRef.current = window.setTimeout(() => {
        // Determine match outcome AFTER damage applied.
        if (newCurrentHp <= 0 && newOpponentHp <= 0) {
          play("lose");
          match.finish(null);
          return;
        }
        if (newOpponentHp <= 0) {
          play("win");
          match.finish(current);
          return;
        }
        if (newCurrentHp <= 0) {
          play("win");
          match.finish(opponent);
          return;
        }
        match.nextTurn();
        setPhase({ kind: "handoff", nextPlayer: opponent });
      }, RESULT_PAUSE_MS);
    },
    [phase, match],
  );

  // Timer expiry counts as null answer.
  const onTimerExpire = useCallback(() => {
    submitAnswer(null);
  }, [submitAnswer]);

  // -------- match controls --------

  const handleRematch = () => {
    if (handoffTimerRef.current) window.clearTimeout(handoffTimerRef.current);
    match.start(match.state.players[0].name, match.state.players[1].name);
    setAskedIds(new Set());
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (handoffTimerRef.current) window.clearTimeout(handoffTimerRef.current);
    match.reset();
    setPhase(null);
    setAskedIds(new Set());
  };
  const handleExit = () => navigate("/play");

  const toggleCategory = (cat: DuelCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        if (next.size === 1) return prev; // never let user disable all
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  const inSuddenDeath = useMemo(
    () =>
      match.state.status === "playing" &&
      isSuddenDeath(match.state.turnNumber, match.state.stats[0], match.state.stats[1]),
    [match.state.status, match.state.turnNumber, match.state.stats],
  );

  // -------- render --------

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Chem Duel"
        subtitle="HP-based trivia. Fast correct = big damage. Wrong = backfire. First to 0 HP loses."
        headerActions={<GameMenu gameKey="chem-duel" />}
      >
        <PlayerSetup
          title="Set up the duel"
          description="Two players, one device. Take turns answering. 100 HP each."
          ctaLabel="Start duel"
          onStart={startMatch}
        />
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];

  return (
    <GameLayout
      title="Chem Duel"
      subtitle="Correct ≤4s deals 20 dmg · slower correct deals 12 · wrong = 8 self-dmg · timeout = 5 self-dmg."
      headerActions={<GameMenu gameKey="chem-duel" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="HP" statMax={STARTING_HP} />
      </div>

      {/* Category filter - collapsed during turn for focus */}
      {phase?.kind === "handoff" && (
        <div className="mb-4 rounded-lg border border-border bg-card/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Categories in play
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ALL_CATEGORIES.map((cat) => {
              const on = enabledCategories.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  aria-pressed={on}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    on
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-background text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Handoff card */}
      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          {inSuddenDeath && (
            <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full bg-destructive/10 text-destructive text-[11px] font-semibold uppercase tracking-wider">
              <Flame size={11} aria-hidden="true" />
              Sudden death - hard questions only
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-4">{current.name}</p>
          <Button onClick={beginTurn} size="lg">
            <Sword size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
          <p className="text-[11px] text-muted-foreground mt-3">
            You'll have {TURN_SECONDS} seconds once the question appears.
          </p>
        </div>
      )}

      {/* Answering card */}
      {phase?.kind === "answering" && !matchOver && (
        <div className="rounded-xl border border-primary/40 bg-card p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]">
                {CATEGORY_LABELS[phase.question.category]}
              </Badge>
              <Badge variant="secondary" className="text-[10px]">
                Difficulty {phase.question.difficulty}
              </Badge>
              <span className="text-[11px] text-muted-foreground">
                {current.name}'s turn
              </span>
            </div>
            <TurnTimer
              key={`q-${phase.question.id}`}
              seconds={TURN_SECONDS}
              onExpire={onTimerExpire}
            />
          </div>

          <h2 className="text-base font-semibold text-foreground mb-4 leading-snug">
            {phase.question.prompt}
          </h2>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            role="group"
            aria-label="Answer choices"
          >
            {phase.question.choices.map((choice, i) => (
              <button
                key={i}
                type="button"
                onClick={() => submitAnswer(i)}
                className="text-left p-3 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded text-[10px] font-bold bg-muted text-muted-foreground">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-sm text-foreground">{choice}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result card */}
      {phase?.kind === "result" && !matchOver && (
        <div
          className={`rounded-xl border p-5 ${
            phase.resolved.opponentDamage > 0
              ? "border-primary/40 bg-primary/5"
              : "border-destructive/30 bg-destructive/5"
          }`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 mb-3">
            {phase.resolved.opponentDamage > 0 ? (
              <CheckCircle2 size={16} className="text-primary" aria-hidden="true" />
            ) : (
              <XCircle size={16} className="text-destructive" aria-hidden="true" />
            )}
            <span className="text-sm font-semibold text-foreground">
              {phase.resolved.outcome === "correct-fast" && `Fast & correct! ${phase.resolved.opponentDamage} dmg dealt.`}
              {phase.resolved.outcome === "correct-slow" && `Correct! ${phase.resolved.opponentDamage} dmg dealt.`}
              {phase.resolved.outcome === "wrong" && `Wrong - ${phase.resolved.selfDamage} self-dmg.`}
              {phase.resolved.outcome === "timeout" && `Time's up - ${phase.resolved.selfDamage} self-dmg.`}
            </span>
          </div>

          <p className="text-sm text-foreground mb-2">
            <span className="text-muted-foreground">Correct answer:</span>{" "}
            <span className="font-medium">
              {String.fromCharCode(65 + phase.question.correctIndex)}.{" "}
              {phase.question.choices[phase.question.correctIndex]}
            </span>
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {phase.question.explanation}
          </p>

          <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
            {phase.resolved.opponentDamage > 0 && (
              <span className="inline-flex items-center gap-1">
                <Sword size={11} aria-hidden="true" />
                {phase.resolved.opponentDamage} → opponent
              </span>
            )}
            {phase.resolved.selfDamage > 0 && (
              <span className="inline-flex items-center gap-1 text-destructive">
                <Heart size={11} aria-hidden="true" />
                −{phase.resolved.selfDamage} self
              </span>
            )}
          </div>
        </div>
      )}

      {/* Initial state safety net */}
      {phase === null && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Skull size={20} className="mx-auto text-muted-foreground mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Setting up the duel…</p>
        </div>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={handleExit}
        detail={`First to 0 HP loses. Started with ${STARTING_HP} HP each.`}
      />
    </GameLayout>
  );
}